import apiService from "./apiService"; // Import the reusable API service

const PAYMENT_API_URL = "/payments"; // Base endpoint for payment operations

/**
 * Create a new payment.
 * @param {Object} paymentData - The payment data to be saved.
 * @returns {Object} - The response from the server.
 */
export const createPayment = async (paymentData) => {
  try {
    const response = await apiService.post(PAYMENT_API_URL, paymentData);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error creating payment:", error.message);
    throw error;
  }
};

/**
 * Fetch all payments with optional pagination.
 * @param {number} page - The page number to fetch.
 * @param {number} limit - The number of payments per page.
 * @returns {Object} - The response data containing payments and metadata.
 */
export const getAllPayments = async (page = 1, limit = 10) => {
  try {
    const response = await apiService.get(PAYMENT_API_URL, {
      params: { page, limit },
    });
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching payments:", error.message);
    throw error;
  }
};

/**
 * Fetch a specific payment by ID.
 * @param {number} paymentId - The ID of the payment to fetch.
 * @returns {Object} - The payment data.
 */
export const getPaymentById = async (paymentId) => {
  try {
    const response = await apiService.get(`${PAYMENT_API_URL}/${paymentId}`);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error fetching payment:", error.message);
    throw error;
  }
};

/**
 * Fetch payments for a specific customer by their ID.
 * @param {number} customerId - The ID of the customer whose payments are to be fetched.
 * @returns {Object[]} - An array of payment records.
 */
export const getPaymentsByCustomerId = async (customerId) => {
  if (!customerId) {
    throw new Error("Customer ID is required");
  }

  try {
    const response = await apiService.get(
      `${PAYMENT_API_URL}/customer/${customerId}`
    );
    if (response.data.success) {
      return response.data; // Return the payment data on success
    } else {
      throw new Error(response.data.message || "Failed to fetch payments");
    }
  } catch (error) {
    console.error("Error fetching payments by customer ID:", error.message);
    throw error;
  }
};

/**
 * Update an existing payment by ID.
 * @param {number} paymentId - The ID of the payment to update.
 * @param {Object} paymentData - The updated payment data.
 * @returns {Object} - The response from the server.
 */
export const updatePayment = async (paymentId, paymentData) => {
  try {
    const response = await apiService.put(
      `${PAYMENT_API_URL}/${paymentId}`,
      paymentData
    );
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error updating payment:", error.message);
    throw error;
  }
};

/**
 * Delete a specific payment by ID.
 * @param {number} paymentId - The ID of the payment to delete.
 * @returns {Object} - The response from the server.
 */
export const deletePayment = async (paymentId) => {
  try {
    const response = await apiService.delete(`${PAYMENT_API_URL}/${paymentId}`);
    return response.data; // Return the response data
  } catch (error) {
    console.error("Error deleting payment:", error.message);
    throw error;
  }
};
